/**
 * Work → Sales. What needs doing for the sales in progress (buyers to call back, deliveries, holds that
 * ran out), then each sale with its readiness, then buyers, deliveries and purchases.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api } from "../../../web/lib/api";
import { formatDate, formatMoney, relativeDay } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { RefPicker } from "../../../web/forms/pickers";
import { SchemaForm, issuesToErrors, type Errors, type Values } from "../../../web/forms/SchemaForm";
import { Page } from "../../../web/shell/Layout";
import { useOpenComposer } from "../../../web/shell/Composer";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Row, Section, Segmented, Sheet, TextInput, toast } from "../../../web/ui/kit";
import { ApiError } from "../../../web/lib/api";
import { OFFERING_FIELDS } from "../contract";
import type { BuyerActivity, DeliveryDTO, OfferingCard, SalesOverview } from "../shared/types";
import { act, kindLabel, LotStatusChip, post, priceText, ReadinessPill, recordSale, useToday } from "./common";

export function SalesHome() {
  useLanguage();
  const [params, setParams] = useSearchParams();
  const q = useApi<SalesOverview>("/m/sales/overview");
  const can = useCan();
  const openComposer = useOpenComposer();
  const today = useToday();
  const [starting, setStarting] = useState(false);
  const listing = params.get("list");
  useEffect(() => {
    if (params.get("view") === "buyers") document.getElementById("sales-buyers")?.scrollIntoView({ behavior: "smooth" });
  }, [params, q.data]);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const d = q.data;
  const followDue = d.follow_ups.filter((a) => a.follow_up_on && a.follow_up_on.slice(0, 10) <= today);
  const week = new Date(Date.parse(`${today}T12:00:00Z`) + 7 * 86_400_000).toISOString().slice(0, 10);
  const deliverSoon = d.deliveries.filter((x) => x.due && x.due <= week);
  const lapsed = d.holds.filter((h) => typeof h.data.until === "string" && (h.data.until as string).slice(0, 10) < today);
  const attention = followDue.length + deliverSoon.length + lapsed.length + d.offers.length;
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} title={t("Sales")}
        actions={can("manage") ? <>
          <Button variant="primary" icon="plus" onClick={() => setStarting(true)}>{t("New sale")}</Button>
          <Button icon="packet" to="/work/sales/packets/new">{t("Share records")}</Button>
        </> : undefined} />

      {attention > 0 && (
        <Section title={t("Needs you")} count={attention}>
          <List>
            {d.offers.map((a) => (
              <Row key={a.event_id} icon="coins" tone="sale" to={`/work/jobs/${a.offering.id}?tab=buyers`}
                title={a.data.amount ? t("{{buyer}} offered {{amount}} for lot {{lots}}", { buyer: a.buyer.label, amount: priceText(a.data.amount as never, a.data.price_basis as string), lots: a.lots.join(", ") }) : t("{{buyer}} made an offer on lot {{lots}}", { buyer: a.buyer.label, lots: a.lots.join(", ") })}
                meta={[a.offering.label, typeof a.data.expires === "string" ? t("good until {{date}}", { date: formatDate(a.data.expires) }) : null].filter(Boolean).join(" · ")} />
            ))}
            {followDue.map((a) => <FollowUpRow key={a.event_id} a={a} today={today} />)}
            {deliverSoon.map((x) => <DeliveryRow key={`${x.offering.id}:${x.lot_id}`} x={x} today={today} />)}
            {lapsed.map((h) => (
              <Row key={h.event_id} icon="clock" tone="warn" to={`/work/jobs/${h.offering.id}?tab=buyers`}
                title={t("Hold for {{buyer}} on lot {{lots}} ran out", { buyer: h.buyer.label, lots: h.lots.join(", ") })}
                meta={[formatDate(h.data.until as string), h.offering.label].join(" · ")} />
            ))}
          </List>
        </Section>
      )}

      <Section title={t("Sales in progress")} count={d.offerings.length}>
        {d.offerings.length === 0 ? (
          <Empty icon="gavel" title={t("No sales going")} body={t("Start one for a bull sale, a private treaty listing or a load for the sale barn. Or tell Hondo: “Put the bulls in a spring sale.”")}
            action={can("manage") ? <div className="row-actions"><Button variant="primary" icon="plus" onClick={() => setStarting(true)}>{t("New sale")}</Button><Button icon="mic" onClick={() => openComposer()}>{t("Tell Hondo")}</Button></div> : undefined} />
        ) : <div className="cards sales-cards">{d.offerings.map((o) => <OfferingCardView key={o.id} o={o} today={today} />)}</div>}
      </Section>

      <Section title={t("Buyers to call back")} count={d.follow_ups.length} className="sales-anchor">
        <div id="sales-buyers" />
        {d.follow_ups.length === 0 ? <p className="muted">{t("No buyers waiting on you.")}</p> : <List>{d.follow_ups.map((a) => <FollowUpRow key={a.event_id} a={a} today={today} />)}</List>}
      </Section>

      {(d.holds.length > 0 || d.agreements.length > 0) && (
        <Section title={t("Holds and sales")}>
          <List>
            {d.holds.map((h) => (
              <Row key={h.event_id} icon="clock" tone="warn" to={`/work/jobs/${h.offering.id}?tab=buyers`}
                title={t("Lot {{lots}} held for {{buyer}}", { lots: h.lots.join(", "), buyer: h.buyer.label })}
                meta={[h.offering.label, typeof h.data.until === "string" ? t("until {{date}}", { date: formatDate(h.data.until) }) : null].filter(Boolean).join(" · ")} />
            ))}
            {d.agreements.map((a) => (
              <Row key={a.event_id} icon="handshake" tone="good" to={`/work/jobs/${a.offering.id}?tab=fulfillment`}
                title={t("Lot {{lots}} sold to {{buyer}}", { lots: a.lots.join(", "), buyer: a.buyer.label })}
                meta={[a.offering.label, a.data.price ? formatMoney(a.data.price as never) : null, typeof a.data.delivery_by === "string" ? t("delivery {{date}}", { date: formatDate(a.data.delivery_by) }) : null].filter(Boolean).join(" · ")} />
            ))}
          </List>
        </Section>
      )}

      {d.deliveries.length > 0 && (
        <Section title={t("Deliveries")} count={d.deliveries.length}>
          <List>{d.deliveries.map((x) => <DeliveryRow key={`${x.offering.id}:${x.lot_id}`} x={x} today={today} />)}</List>
        </Section>
      )}

      <Section title={t("Purchases and orders")} count={d.purchases.length} action={<Button size="sm" variant="ghost" to="/work/purchases">{t("All purchases")}</Button>}>
        {d.purchases.length === 0 ? <p className="muted">{t("Nothing on order.")}</p> : (
          <List>{d.purchases.slice(0, 5).map((p) => (
            <Row key={p.id} icon="cart" to={`/work/jobs/${p.id}`} title={p.title}
              meta={[p.supplier?.label, p.ordered?.expected_on ? t("expected {{date}}", { date: formatDate(p.ordered.expected_on) }) : p.needed_by ? t("needed by {{date}}", { date: formatDate(p.needed_by) }) : null].filter(Boolean).join(" · ")} />
          ))}</List>
        )}
      </Section>

      {d.past.length > 0 && (
        <Disclosure label={t("Past sales")} count={d.past.length}>
          <List>{d.past.map((o) => <Row key={o.id} icon="gavel" to={`/work/jobs/${o.id}`} title={o.title} meta={[kindLabel(o.kind), o.sale_date ? formatDate(o.sale_date) : null, plural(o.lots, "{{count}} lot", "{{count}} lots")].filter(Boolean).join(" · ")} />)}</List>
        </Disclosure>
      )}

      <NewOfferingSheet open={starting} onClose={() => setStarting(false)} />
      <ListAnimalSheet animalId={listing} offerings={d.offerings} onClose={() => { params.delete("list"); setParams(params, { replace: true }); }} />
    </Page>
  );
}

function OfferingCardView({ o, today }: { o: OfferingCard; today: string }) {
  const sold = (o.counts.sold ?? 0) + (o.counts.delivered ?? 0);
  return (
    <Card to={`/work/jobs/${o.id}`} className="job-card sales-card">
      <span className="job-kind"><Icon name="gavel" size={14} />{kindLabel(o.kind)}</span>
      <span className="job-title">{o.title}</span>
      <span className="sales-card-line">
        {o.sale_date && <span><Icon name="calendar" size={14} />{relativeDay(o.sale_date, today)}</span>}
        <span>{plural(o.lots, "{{count}} lot", "{{count}} lots")}</span>
        {sold > 0 && <span>{plural(sold, "{{count}} sold", "{{count}} sold")}</span>}
      </span>
      <span className="badges">
        {(o.counts.reserved ?? 0) > 0 && <LotStatusChip status="reserved" />}
        <ReadinessPill r={o.readiness} />
        {o.catalog && <Chip tone={o.catalog.status === "approved" ? "good" : "neutral"} icon="catalog">{o.catalog.status === "approved" ? t("Catalog v{{version}} approved", { version: o.catalog.version }) : t("Catalog proof v{{version}}", { version: o.catalog.version })}</Chip>}
      </span>
    </Card>
  );
}

function FollowUpRow({ a, today }: { a: BuyerActivity; today: string }) {
  const overdue = !!a.follow_up_on && a.follow_up_on.slice(0, 10) < today;
  const done = async () => {
    if (!a.task_id) return;
    await act(() => api.patch(`/tasks/${a.task_id}`, { status: "done" }), t("Done"));
  };
  return (
    <li className="row sales-follow">
      <div className="row-inner">
        <span className="row-icon tone-sale"><Icon name="contact" size={18} /></span>
        <Link to={`/ranch/people/${a.buyer.id}`} className="row-main">
          <span className="row-title">{a.follow_up ? t("{{buyer}}: {{what}}", { buyer: a.buyer.label, what: a.follow_up }) : a.buyer.label}</span>
          <span className="row-meta">
            {a.follow_up_on && <span className={overdue ? "is-overdue" : undefined}>{relativeDay(a.follow_up_on, today)}</span>}
            {a.lots.length > 0 && <span>{t("Lots {{lots}}", { lots: a.lots.join(", ") })}</span>}
            <span>{a.offering.label}</span>
          </span>
        </Link>
        <span className="row-trailing">
          {a.task_id && <Button size="sm" icon="check" onClick={() => void done()}>{t("Done")}</Button>}
        </span>
      </div>
    </li>
  );
}

function DeliveryRow({ x, today }: { x: DeliveryDTO; today: string }) {
  const late = !!x.due && x.due < today;
  return (
    <li className="row">
      <div className="row-inner">
        <span className={late ? "row-icon tone-warn" : "row-icon tone-sale"}><Icon name="truck" size={18} /></span>
        <Link to={`/work/jobs/${x.offering.id}?tab=fulfillment`} className="row-main">
          <span className="row-title">{x.buyer ? t("Lot {{lot}} to {{buyer}}", { lot: x.lot_number ?? "", buyer: x.buyer.label }) : t("Lot {{lot}}, {{sale}}", { lot: x.lot_number ?? "", sale: x.offering.label })}</span>
          <span className="row-meta">
            {x.due && <span className={late ? "is-overdue" : undefined}>{relativeDay(x.due, today)}</span>}
            <span>{plural(x.remaining, "{{count}} head to go", "{{count}} head to go")}</span>
          </span>
        </Link>
        <span className="row-trailing">
          <Button size="sm" icon="truck" onClick={() => recordSale("sales.delivery", { offering: x.offering, lots: x.lot_number ? [x.lot_number] : [], animals: x.animals, buyer: x.buyer })}>{t("Record")}</Button>
        </span>
      </div>
    </li>
  );
}

/** Start a sale: name, kind, date and the cattle, with the rest folded away. */
export function NewOfferingSheet({ open, onClose, animals: initialAnimals = [] }: { open: boolean; onClose: () => void; animals?: string[] }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [data, setData] = useState<Values>({ kind: "production_sale" });
  const [animals, setAnimals] = useState<string[]>(initialAnimals);
  const [mode, setMode] = useState<"one_each" | "one" | "none">("one_each");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setAnimals(initialAnimals); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      const fallback = kindLabel(data.kind as string) || t("Sale");
      const { id } = await post<{ id: string }>("/offerings", { title: title.trim() || fallback, sale_date: date || null, data, animal_ids: animals, lots: mode });
      toast(t("Sale started"), { tone: "good" });
      onClose();
      navigate(`/work/jobs/${id}`);
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("New sale")}>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <Field label={t("Name")}><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Spring Bull Sale")} autoFocus /></Field>
        <Field label={t("Sale date")} optional hint={t("For a sale-barn load, the day it goes.")}><TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <SchemaForm fields={OFFERING_FIELDS} value={data} onChange={setData} errors={errors} />
        <Field label={t("Cattle")} optional hint={t("You can add or change lots later.")}>
          <RefPicker target="animal" multiple value={animals} onChange={(v) => setAnimals((v as string[]) ?? [])} filter={{ status: "current" }} />
        </Field>
        {animals.length > 1 && (
          <Field label={t("Lots")}>
            <Segmented label={t("Lots")} value={mode} onChange={setMode} options={[{ value: "one_each", label: t("One lot each") }, { value: "one", label: t("All in one lot") }, { value: "none", label: t("Decide later") }]} />
          </Field>
        )}
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} icon="check">{t("Start the sale")}</Button><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button></div>
      </form>
    </Sheet>
  );
}

/** "Put up for sale" from an animal's page: pick the sale and the lot number. */
function ListAnimalSheet({ animalId, offerings, onClose }: { animalId: string | null; offerings: OfferingCard[]; onClose: () => void }) {
  const navigate = useNavigate();
  const [offeringId, setOfferingId] = useState<string>("");
  const [number, setNumber] = useState("");
  const [starting, setStarting] = useState(false);
  const label = useApi<{ label: string }>(animalId ? `/animals/${animalId}` : null);
  useEffect(() => { setOfferingId(offerings[0]?.id ?? ""); setNumber(""); }, [animalId, offerings]);
  if (!animalId) return null;
  if (starting) return <NewOfferingSheet open onClose={onClose} animals={[animalId]} />;
  const submit = async () => {
    const ok = await act(() => post(`/offerings/${offeringId}/lots`, { lot_number: number.trim() || undefined, animal_ids: [animalId] }), t("Put up for sale"));
    if (ok) { onClose(); navigate(`/work/jobs/${offeringId}?tab=lots`); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Put {{animal}} up for sale", { animal: label.data?.label ?? "" })}>
      {offerings.length === 0 ? (
        <Empty icon="gavel" title={t("No sales going")} action={<Button variant="primary" icon="plus" onClick={() => setStarting(true)}>{t("Start a sale")}</Button>} />
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <Field label={t("Which sale")}>
            <div className="sales-choice-list" role="radiogroup" aria-label={t("Which sale")}>
              {offerings.map((o) => (
                <button key={o.id} type="button" role="radio" aria-checked={o.id === offeringId} className={o.id === offeringId ? "sales-choice is-on" : "sales-choice"} onClick={() => setOfferingId(o.id)}>
                  <strong>{o.title}</strong><span className="muted">{[kindLabel(o.kind), o.sale_date ? formatDate(o.sale_date) : null].filter(Boolean).join(" · ")}</span>
                </button>
              ))}
            </div>
          </Field>
          <Field label={t("Lot number")} optional hint={t("Leave it empty for the next number.")}><TextInput value={number} onChange={(e) => setNumber(e.target.value)} inputMode="numeric" /></Field>
          <div className="form-actions">
            <Button variant="primary" size="lg" type="submit" icon="tag" disabled={!offeringId}>{t("Put up for sale")}</Button>
            <Button variant="ghost" onClick={() => setStarting(true)}>{t("Start a new sale instead")}</Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
